import { useState, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle } from 'lucide-react';

interface DeleteFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folderName: string;
  workflowCount: number;
  subfolderCount: number;
  onConfirm: () => void;
  isDeleting?: boolean;
}

export function DeleteFolderDialog({
  open,
  onOpenChange,
  folderName,
  workflowCount,
  subfolderCount,
  onConfirm,
  isDeleting = false,
}: DeleteFolderDialogProps) {
  const [confirmText, setConfirmText] = useState('');
  const hasSubfolders = subfolderCount > 0;
  const isConfirmValid = !hasSubfolders || confirmText.toLowerCase() === 'delete';

  // Reset confirmation text when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setConfirmText('');
    }
  }, [open]);

  const handleConfirm = () => {
    if (isConfirmValid && !isDeleting) {
      onConfirm();
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Delete Folder
          </AlertDialogTitle>
          <AlertDialogDescription>
            <div className="space-y-3">
              <p>
                Are you sure you want to delete <strong>"{folderName}"</strong>?
              </p>
              
              {subfolderCount > 0 && (
                <div className="rounded-md bg-destructive/10 p-3 space-y-2">
                  <p className="text-sm font-medium text-destructive">
                    Warning: This folder contains:
                  </p>
                  <ul className="text-sm space-y-1 ml-4 list-disc">
                    <li>
                      {subfolderCount} subfolder{subfolderCount !== 1 ? 's' : ''}
                    </li>
                  </ul>
                  <p className="text-sm text-muted-foreground mt-2">
                    You must move or delete all subfolders before deleting this folder.
                  </p>
                </div>
              )}
              
              {workflowCount > 0 && subfolderCount === 0 && (
                <div className="rounded-md bg-amber-500/10 p-3 space-y-2">
                  <p className="text-sm font-medium text-amber-600 dark:text-amber-500">
                    This folder contains {workflowCount} workflow{workflowCount !== 1 ? 's' : ''}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    All workflows will be moved to the root folder before deletion.
                  </p>
                </div>
              )}

              {!hasSubfolders && workflowCount === 0 && (
                <p className="text-sm text-muted-foreground">
                  This action cannot be undone.
                </p>
              )}

              {hasSubfolders && (
                <div className="space-y-2 pt-2">
                  <Label htmlFor="confirm-delete" className="text-sm font-medium">
                    Type <strong>delete</strong> to confirm:
                  </Label>
                  <Input
                    id="confirm-delete"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="delete"
                    className="font-mono"
                    autoComplete="off"
                    disabled={isDeleting}
                  />
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={!isConfirmValid || isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? 'Deleting...' : 'Delete Folder'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
